import axios from 'axios';
import { User } from '../types/user';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('angel-cite-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('angel-cite-token');
      localStorage.removeItem('angel-cite-auth');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Add these functions

export const uploadPaymentProof = async (orderId: string, file: File) => {
  // In a real implementation, you would:
  // 1. Convert file to base64 or FormData
  // 2. Send to your backend API
  // 3. Handle the response

  const formData = new FormData();
  formData.append('orderId', orderId);
  formData.append('paymentProof', file);

  // Simulate API call
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log('Payment proof uploaded:', { orderId, fileName: file.name });
      resolve({ success: true, orderId });
    }, 1000);
  });
};

export const confirmEcoCashPayment = async (orderId: string, _ecocashNumber: string, _amount: number) => {
  // This would integrate with a payment gateway in production
  // Log the payment details for debugging
  console.log(`Processing EcoCash payment: Order ${orderId}`);
  
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        success: true,
        orderId,
        transactionId: `ECOCASH-${Date.now()}`,
        timestamp: new Date().toISOString(),
      });
    }, 1500);
  });
};

// WhatsApp order notification
export const sendOrderWhatsAppNotification = async (
  orderId: string,
  customerName: string,
  customerPhone: string,
  customerEmail: string,
  address: string,
  city: string,
  zipCode: string,
  paymentMethod: string,
  items: Array<{ name: string; quantity: number; price: number }>,
  subtotal: number,
  shipping: number,
  tax: number,
  total: number
): Promise<void> => {
  // Format items list with URL-encoded newlines
  const itemsList = items
    .map(item => `* ${item.name} - Qty: ${item.quantity} x $${item.price.toFixed(2)} = $${(item.quantity * item.price).toFixed(2)}`)
    .join('%0A');

  // Build the message with proper URL encoding for emojis and special characters
  const message = 
    `%F0%9F%9B%92%20NEW%20ORDER%20%23${orderId}%0A%0A` +
    `%F0%9F%91%A4%20Customer%3A%20${encodeURIComponent(customerName)}%0A` +
    `%F0%9F%93%B1%20Phone%3A%20${encodeURIComponent(customerPhone)}%0A` +
    `%F0%9F%93%A7%20Email%3A%20${encodeURIComponent(customerEmail)}%0A` +
    `%F0%9F%97%A3%20Address%3A%20${encodeURIComponent(address)}%2C%20${encodeURIComponent(city)}%20${encodeURIComponent(zipCode)}%0A` +
    `%F0%9F%92%B3%20Payment%3A%20${encodeURIComponent(paymentMethod)}%0A%0A` +
    `%F0%9F%93%A6%20Items%3A%0A${itemsList}%0A%0A` +
    `%F0%9F%92%B0%20Subtotal%3A%20$${subtotal.toFixed(2)}%0A` +
    `%F0%9F%9A%9A%20Shipping%3A%20$${shipping.toFixed(2)}%0A` +
    `%F0%9F%93%8A%20Tax%3A%20$${tax.toFixed(2)}%0A` +
    `%E2%9C%85%20Total%3A%20$${total.toFixed(2)}`;

  // Open WhatsApp with the message
  window.open(`https://wa.me/23057432920?text=${message}`, '_blank');

  console.log('WhatsApp notification sent for order:', orderId);
};

// Auth API functions
export const registerUser = async (userData: {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
}): Promise<{ message: string; userId: string }> => {
  const response = await api.post('/auth/register', userData);
  return response.data;
};

export const verifyEmail = async (token: string): Promise<{ message: string }> => {
  const response = await api.get('/auth/verify-email', { params: { token } });
  return response.data;
};

export const loginUser = async (email: string, password: string): Promise<{
  message: string;
  user: User;
  token: string;
}> => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  const response = await api.post('/auth/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  const response = await api.post('/auth/reset-password', { token, newPassword });
  return response.data;
};

export const getUserProfile = async (email: string): Promise<User> => {
  const response = await api.get('/auth/profile', { params: { email } });
  return response.data;
};

export const updateUserProfile = async (data: Partial<User>): Promise<{ message: string; user: User }> => {
  const response = await api.put('/auth/profile', data);
  return response.data;
};

export default api;

